<template>
  <div class="search-native">
    <div class="search-native__header col l2">
      <h2
        class="search-native__title"
        v-text="
          $string('search.title')
        "
      />
      <button
        v-if="searchActive"
        type="button"
        class="search-native__close"
        @click.prevent="clearSearch(true)"
      >
        <icon-close />
      </button>
    </div>
    <div class="search-native__input">
      <input
        ref="search"
        v-model="query"
        class="search-native__input-field"
        type="text"
        tabindex="-1"
        :placeholder="$string('search.placeholder')"
        @focus="handleInputFocus"
        @blur="handleInputBlur"
        @input="handleInputChange"
        @keydown.enter="gotoSearch"
      >
      <span class="search-native__arrow">
        <icon-arrow-right />
      </span>
      <span class="search-native__icon">
        <icon-search />
      </span>
      <button
        v-if="searchStatus === 'done'"
        type="button"
        class="search-native__clear"
        @click="clearSearch(true)"
        v-text="$string('search.clear')"
      />
    </div>
    <div class="search-native__desktop-clear">
      <button
        type="button"
        @click="closeSearch"
      >
        <icon-close />
      </button>
    </div>
    <div
      v-if="popularTerms.length && results.length <= 0 && searchActive && searchStatus === 'ready'"
      class="search-native__popular"
    >
      <p
        class="search-native__popular-header text-body-s-bold"
        v-text="$string('search.popular_header')"
      />
      <ul class="search-native__popular-terms">
        <li
          v-for="(term, index) of popularTerms"
          :key="`popular-${index}`"
          class="search-native__popular-term text-body-s"
          tabindex="0"
          data-search="popular_term"
          @click.prevent="setSearch(term)"
          v-text="term"
        />
      </ul>
    </div>
    <div
      v-if="results.length > 0"
      class="search-native__suggestions"
    >
      <p
        class="search-native__suggestions-header text-body-s-bold"
        v-text="$string('search.suggestions_header')"
      />
      <ul class="search-native__suggestions-list">
        <li
          v-for="suggestion of suggestions"
          :key="suggestion.id"
          class="search-native__suggestion"
        >
          <a
            class="text-body-s search-native__suggestion"
            :href="getProductUrl(suggestion)"
            v-html="formatSuggestion(suggestion.title)"
          />
        </li>
      </ul>
    </div>
    <p
      v-if="results.length > 0 && searchStatus === 'done'"
      class="search-native__count results"
      v-html="resultsString"
    />
    <p
      v-if="results.length === 0 && searchStatus === 'done'"
      class="search-native__count no-results"
      v-html="noResultsString"
    />
    <div
      v-if="results.length > 0 && searchStatus === 'done'"
      class="search-native__results"
    >
      <div class="search-native__results-grid">
        <product-card
          v-for="(product, index) of results.slice(0,4)"
          :key="product.handle"
          :class="{ wide: (index+1) % 3 === 0}"
          :product="product"
          padding
        />
      </div>
    </div>
    <div
      v-if="results.length > 0 && searchStatus === 'done'"
      class="search-native__results-button"
    >
      <button
        type="button"
        class="search-native__button button button--boxed text-body-s"
        @click.prevent="gotoSearch"
        v-text="allResultsString"
      />
    </div>
    <div class="search-native__desktop-results">
      <product-card
        v-for="product of results.slice(0, 4)"
        :key="product.handle"
        :product="product"
        small
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: search (search-native)
 * -----------------------------------------------------------------------------
 * Native search component with input and results.
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'
import { getResults } from '~/helpers/search'
import IconArrowRight from '~icons/direction/arrow-drop/right.svg'
import IconSearch from '~icons/misc/search.svg'
import IconClose from '~icons/misc/close.svg'

import ProductCard from '~async/product-card/product-card'
import { debounce } from '~/helpers/general'
import { nextTick } from 'vue'

export default {
  name: 'SearchNative',

  components: {
    IconArrowRight,
    IconSearch,
    ProductCard,
    IconClose,
  },

  props: {
    isActive: {
      type: Boolean,
      default: false,
    },

    vipCustomer: {
      type: Boolean,
      default: false,
    }
  },

  data() {
    return {
      query: '',
      results: [],
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      settings: (state) => state.index.settings,
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Map Vuex Getters.
     */
    ...mapGetters({
      searchStatus: 'search/getSearchStatus',
      searchActive: 'search/getSearchActive',
    }),

    /**
     * Returns popular terms that are set in theme settings
     * @returns {Array}
     */
    popularTerms() {
      return cnvs.search.popularTerms
    },

    /**
     * Grabs the suggestions from the results, count set in theme settings
     * @returns {Array}
     */
    suggestions() {
      if (this.results.length === 0) {
        return []
      }

      const suggestions = this.results.slice(0, cnvs.search.suggestions)

      return suggestions
    },

    /**
     * Transform the query results string
     * @returns {String}
     */
    resultsString() {
      return this.$string('search.results_html')
        .replace('{total}', this.results.length)
        .replace('{query}', this.query)
    },

    /**
     * Transforms the string for no results to include the query
     * @returns {String}
     */
    noResultsString() {
      return this.$string('search.no_results_html')
        .replace('{query}', this.query)
    },

    /**
     * Transforms the string for the view all results button text
     */
    allResultsString() {
      return this.$string('search.view_results').replace('{total}', this.results.length)
    },
  },

  watch: {

    /**
     * Watches the query value and when it changes call the
     * debounceSearch function
     */
    query() {
      this.setSearchStatus('loading')

      if (this.query === '') {
        this.results = []
        this.setSearchStatus('ready')
        return
      }

      this.debounceSearch()
    },

    isActive() {
      if (this.isActive) {
        nextTick().then(() => {
          this.$refs.search.focus()
        })
      }
    },

    searchActive(newValue) {
      if (!newValue) {
        this.clearSearch()
      }
    },
  },

  mounted() {
    this.debounceSearch = debounce(this.performSearch, this.settings.throttle)
  },

  methods: {
    ...mapActions({
      setSearchActive: 'search/setSearchActive',
      setSearchStatus: 'search/setSearchStatus',
      closeOverlay: 'overlays/close',
    }),

    closeSearch() {
      this.clearSearch(false)
      this.closeOverlay()
    },

    /**
     * Goes to the search results page
     */
    gotoSearch() {
      let route = window.cnvs.routes.root

      if (route !== '/') {
        route = `${route}/`
      }

      const url = `${route}search?q=${this.query}&type=product&sort_key=relevance`

      window.location = url
    },

    /**
     * Handles the focus event on the search input
     */
    handleInputFocus() {
      this.setSearchActive(true)
      this.setSearchStatus('ready')
    },

    /**
     * Handles the blur event on the search input
     * @param {Object} event the event Object
     */
    handleInputBlur(event) {
      const { relatedTarget } = event
      const dataset = relatedTarget?.dataset.search

      if (dataset === 'popular_term' || this.query !== '') {
        return
      }

      if (this.isMobile) {
        this.clearSearch(false)
        return
      }

      this.clearSearch(true)
    },

    /**
     * Clears the Search and sets the status back to ready.
     * If searchActive is true retains focus on the search input
     * so that the popular terms still show.
     * @param {Boolean} searchActive Does the input retain focus after clearing
     */
    clearSearch(searchActive = false) {
      this.results = []
      this.query = ''
      this.setSearchStatus('ready')
      this.setSearchActive(searchActive)

      if (searchActive) {
        nextTick().then(() => {
          this.$refs.search.focus()
        })
      }
    },

    /**
     * Clicking on a popular term sets the query to that term and performs the
     * search
     * @param {String} query String to set the query too
     */
    setSearch(query) {
      this.query = query
    },

    /**
     * Performs the search and sets the results
     */
    async performSearch() {
      const variables = {
        country: cnvs.store.country,
        language: cnvs.store.language,
        isVip: this.vipCustomer,
        query: this.query,
        limit: 10,
      }

      const results = await getResults(variables)

      this.setSearchStatus('done')

      this.results = results

    },

    /**
     * Formats the suggestion to show the query in bold, regex is used to ensure
     * it is case insensitive
     * @param {String} suggestion the suggestion string being replaced
     */
    formatSuggestion(suggestion) {
      const regex = new RegExp(this.query, 'ig')

      return suggestion.replaceAll(
        regex,
        (match) => {
          return `<span class="text-body-s-bold">${match}</span>`
        },
      )
    },

    /**
     * Get Product URL
     * @param {Object} product The Product
     */
    getProductUrl(product) {
      return `${cnvs.store.shopUrl}/products/${product.handle}`
    },
  },
}
</script>

<style lang="scss">
@import './search-native';
</style>
